// SPDX-License-Identifier: AGPL-3.0-or-later

import {applyComposerHolidayPeekSeasonRates} from '@app/features/holiday/components/ComposerHolidayPeekSeasonRates';
import type {ComposerHolidayPeekMachineInput} from '@app/features/holiday/components/ComposerHolidayPeekStateMachine';
import type {HolidayPackId} from '@app/features/holiday/state/HolidayPreferences';
import type {ComponentType, SVGProps} from 'react';

export interface ComposerHolidayPeekStage {
	id: string;
	Svg: ComponentType<SVGProps<SVGSVGElement>>;
	height: number;
	bodyColor: string;
}

export interface ComposerHolidayPeekCrop {
	widthRem: number;
	rightRem: number;
	artWidth: number;
	ledgeUnits: number;
	ledgeExtraPx: number;
}

export interface ComposerHolidayPeekPack {
	id: string;
	/** Seasonal packs beat the year-round baseline when both are enabled. */
	seasonal: boolean;
	stages: Record<string, ComposerHolidayPeekStage>;
	ladder: ReadonlyArray<string>;
	crop: ComposerHolidayPeekCrop;
	machineInput: ComposerHolidayPeekMachineInput;
	/** Pack-specific rate quirks only. The season rule is applied for every pack, not here. */
	resolveMachineInput?: () => ComposerHolidayPeekMachineInput;
	/** Season window that governs this pack's rates. Omit for a year-round baseline pack. */
	seasonPackId?: HolidayPackId;
	/** Ceiling held over every chance curve outside that season's peak. Needs `seasonPackId`. */
	offPeakChanceCap?: number;
	isEnabled: () => boolean;
	forceArtLoop?: () => boolean;
	matchInterrupt?: (value: string) => string | null;
}

const packs: Array<ComposerHolidayPeekPack> = [];

export function registerComposerHolidayPeekPack(pack: ComposerHolidayPeekPack): () => void {
	if (!packs.includes(pack)) {
		packs.push(pack);
	}
	return () => {
		const index = packs.indexOf(pack);
		if (index >= 0) packs.splice(index, 1);
	};
}

function packIsEnabled(pack: ComposerHolidayPeekPack): boolean {
	try {
		return pack.isEnabled();
	} catch {
		return false;
	}
}

export function resolveComposerHolidayPeekPack(): ComposerHolidayPeekPack | null {
	const enabled = packs.filter(packIsEnabled);
	return enabled.find((pack) => pack.seasonal) ?? enabled.find((pack) => !pack.seasonal) ?? null;
}

/**
 * Machine input for a resolved pack: the pack's own rate quirks, then the shared season rule.
 * Packs declare `seasonPackId` / `offPeakChanceCap` and the holiday system applies them, so a new
 * pack cannot forget to scale itself down outside peak.
 */
export function resolveComposerHolidayPeekMachineInput(
	pack: ComposerHolidayPeekPack,
	now: Date = new Date(),
): ComposerHolidayPeekMachineInput {
	const base = pack.resolveMachineInput?.() ?? pack.machineInput;
	if (pack.seasonPackId == null || pack.offPeakChanceCap == null) return base;
	return applyComposerHolidayPeekSeasonRates(base, pack.seasonPackId, now, {
		offPeakChanceCap: pack.offPeakChanceCap,
	});
}

export function peekHeightRatio(height: number, artWidth: number): number {
	return height / artWidth;
}

export function peekLedgeRatio(ledgeUnits: number, artWidth: number): number {
	return ledgeUnits / artWidth;
}

export function composerHolidayPeekStageIds(pack: ComposerHolidayPeekPack): Array<string> {
	const ids: Array<string> = [];
	const seen = new Set<string>();
	for (const id of pack.ladder) {
		if (pack.stages[id] == null || seen.has(id)) continue;
		seen.add(id);
		ids.push(id);
	}
	for (const id of Object.keys(pack.stages)) {
		if (seen.has(id)) continue;
		seen.add(id);
		ids.push(id);
	}
	return ids;
}
